import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useClinicContext } from './useClinicContext';
import { useClinicPortalServiceType } from './clinicPortalServiceType';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';
import {
  fetchReturnRezeptList,
  cancelReturnRezepts,
  updateResubmissionMonth,
  startEditingReturnRezepts,
} from '@/services/main/returnRezept/returnRezeptService';
import type {
  CancelReturnPayload,
  ResubmitPayload,
} from '@/services/main/returnRezept/types';

const QUERY_KEY = 'return-rezept';
const SUCCESS_MSG = '更新が正常に終了しました。';
const ERROR_MSG = '更新ができませんでした。';
const FETCH_ERROR_MSG = 'データ取得に失敗しました。';
const START_EDITING_SUCCESS_MSG = '編集を開始しました。';
const START_EDITING_ERROR_MSG = '編集を開始できませんでした。';

export function useReturnRezeptList() {
  const { tenant, clinicNumber } = useClinicContext();
  // serviceType (A2/R2) is sent via the X-Service-Type header (axios interceptor), so it never
  // shows up in queryFn's arguments — but the list it returns differs per service. It must be in
  // the cache key, otherwise toggling A↔R in the sidebar reuses the other service's rows while the
  // column labels already switched (HIT-11387).
  const serviceType = useClinicPortalServiceType();
  const toast = useAppToast();

  const { data, isLoading, isError, isPending, refetch, error } = useQuery({
    queryKey: [QUERY_KEY, 'list', tenant, clinicNumber, serviceType],
    queryFn: () => fetchReturnRezeptList(tenant ?? '', clinicNumber ?? ''),
    enabled: !!tenant && !!clinicNumber,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, FETCH_ERROR_MSG));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, isPending, refetch, error };
}

export function useCancelReturn() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  const { mutateAsync: cancelReturnMutation, isPending: isLoading } = useMutation({
    mutationFn: (payload: CancelReturnPayload) =>
      cancelReturnRezepts(tenant ?? '', clinicNumber ?? '', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success(SUCCESS_MSG);
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, ERROR_MSG));
    },
  });

  return { cancelReturnMutation, isLoading };
}

export function useUpdateResubmission() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  const { mutateAsync: updateResubmissionMutation, isPending: isLoading } = useMutation({
    mutationFn: (payload: ResubmitPayload) =>
      updateResubmissionMonth(tenant ?? '', clinicNumber ?? '', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success(SUCCESS_MSG);
    },
    onError: err => {
      if (err instanceof AxiosError && err.response?.status === 422) {
        toast.error(ERROR_MSG);
        return;
      }
      toast.error(getApiErrorMessage(err, ERROR_MSG));
    },
  });

  return { updateResubmissionMutation, isLoading };
}

/**
 * RS-22 — unlock the visits of returned rezepts so they can actually be edited.
 *
 * The server's own sentence is shown on failure: a 422 here is not a fault but an explanation — every
 * selected rezept either is not a 返戻, or shares its visits with one that is still 確定.
 */
export function useStartEditing() {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  const { mutateAsync: startEditingMutation, isPending: isLoading } = useMutation({
    mutationFn: (payload: CancelReturnPayload) =>
      startEditingReturnRezepts(tenant ?? '', clinicNumber ?? '', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success(START_EDITING_SUCCESS_MSG);
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, START_EDITING_ERROR_MSG));
    },
  });

  return { startEditingMutation, isLoading };
}
